import * as THREE from 'three';

// Triplanar projection + procedural detail — Phases 1 & 3 of the rendering
// roadmap (docs/SHIP-RENDERING-ARCHITECTURE.md §2). Kills UV toil (no
// unwraps, no seams at module boundaries, one density number per material)
// and moves Substance-designer logic into the shader:
//
//   EDGE WEAR — curvature detected per-pixel from screen-space derivatives of
//   the object-space normal (normalized by position derivatives, so it's
//   view-stable): convex edges chip down to bare metal, broken up by noise so
//   wear is patchy, not uniform. Painted livery materials chip too.
//
//   GRIME — 2-octave value-noise mottling, anisotropically stretched along
//   the spine (+Z) so it reads as thermal/vent streaking; darkens albedo
//   toward a sooty brown and raises roughness.
//
// Projection space is OBJECT space (the mesh's own geometry space), NOT world
// space — ships fly and radiator panels hinge, so textures must stick to each
// mesh rather than swim as it moves. A side effect we want: a 0.45-scale Pup
// scales its surface features down with it, so it reads as a smaller ship.
//
// `worldScale` opts OUT of that side effect for meshes that share one geometry
// across a wide size range (the combat bubble's rocks, src/scene/asteroids.ts):
// object space is multiplied by the model matrix's own scale, so features hold
// a constant WORLD size and a big rock stops reading as a close-up of a small
// one. Scale only — rotation leaves a matrix column's length alone, so a
// tumbling rock's surface still sticks to it.
//
// Normal maps use the whiteout blend (Ben Golus, "Normal Mapping for a
// Triplanar Shader"): perturb per-axis in tangent-ish space, swizzle into
// object space, blend, then carry into view space via normalMatrix. We declare
// `uniform mat3 normalMatrix` in the fragment shader ourselves — the renderer
// uploads it per-object regardless of stage, it just isn't in three's default
// fragment prelude.
//
// The wear/grime masks are computed ONCE (in the map stage) and reused by the
// roughness stage — same main() scope. Materials without maps (flat livery
// paint) still get masks: the map-stage replacement always executes.

export interface TriplanarOptions {
  /** Texture tiles per object-space unit (density). Default 0.5. */
  scale?: number;
  /** Blend exponent — higher pinches the 3-way blend tighter at 45° corners. Default 4. */
  sharpness?: number;
  /** Edge-wear amount 0..1 (0 = pristine). Default 0. */
  wear?: number;
  /** Grime amount 0..1 (0 = clean). Default 0. */
  grime?: number;
  /** Bare metal revealed where paint/coating chips. Default worn steel. */
  wearColor?: THREE.Color | number;
  /**
   * Grime's per-axis noise frequency. The default squashes +Z so grime reads as
   * thermal streaking off a ship's spine; a tumbling rock has no spine, so pass
   * something isotropic for one.
   */
  grimeAniso?: THREE.Vector3;
  /** Mineral streak colour — the third hue, over the map/vertex-colour base. */
  accentColor?: THREE.Color | number;
  /** Streak amount 0..1 (0 = none). Default 0. */
  accent?: number;
  /** Streak noise frequency, per object-space unit. Default 2.3. */
  accentScale?: number;
  /**
   * Offset into every noise domain. Meshes sharing one geometry project the
   * same noise onto the same vertices; a per-instance offset is what stops a
   * dozen rocks off one variant from wearing identical streaks.
   */
  seed?: THREE.Vector3;
  /** Project in object space scaled to world size. Default false. */
  worldScale?: boolean;
}

export function applyTriplanar(mat: THREE.MeshStandardMaterial, opts: TriplanarOptions = {}): void {
  const scale = opts.scale ?? 0.5;
  const sharpness = opts.sharpness ?? 4.0;
  const wear = opts.wear ?? 0;
  const grime = opts.grime ?? 0;
  const wearColor = new THREE.Color(opts.wearColor ?? 0xb9bec4);
  const grimeAniso = opts.grimeAniso ?? new THREE.Vector3(0.9, 0.9, 0.28);
  const accentColor = new THREE.Color(opts.accentColor ?? 0x000000);
  const accent = opts.accent ?? 0;
  const accentScale = opts.accentScale ?? 2.3;
  const seed = opts.seed ?? new THREE.Vector3();
  const worldScale = opts.worldScale ? 1 : 0;

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTriScale = { value: scale };
    shader.uniforms.uTriSharp = { value: sharpness };
    shader.uniforms.uWearAmt = { value: wear };
    shader.uniforms.uGrimeAmt = { value: grime };
    shader.uniforms.uWearColor = { value: wearColor };
    shader.uniforms.uGrimeAniso = { value: grimeAniso };
    shader.uniforms.uAccentColor = { value: accentColor };
    shader.uniforms.uAccentAmt = { value: accent };
    shader.uniforms.uAccentScale = { value: accentScale };
    shader.uniforms.uTriSeed = { value: seed };
    shader.uniforms.uTriWorldScale = { value: worldScale };

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vTriPos;
varying vec3 vTriNorm;
uniform float uTriWorldScale;`,
      )
      // beginnormal_vertex (objectNormal) runs before begin_vertex (transformed)
      // modelMatrix is in three's own vertex prelude; a column's length is the
      // mesh's scale on that axis, untouched by however it is rotated.
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vTriPos = transformed * mix( 1.0, length( modelMatrix[ 0 ].xyz ), uTriWorldScale );
vTriNorm = objectNormal;`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vTriPos;
varying vec3 vTriNorm;
uniform float uTriScale;
uniform float uTriSharp;
uniform float uWearAmt;
uniform float uGrimeAmt;
uniform vec3 uWearColor;
uniform vec3 uGrimeAniso;
uniform vec3 uAccentColor;
uniform float uAccentAmt;
uniform float uAccentScale;
uniform vec3 uTriSeed;
uniform mat3 normalMatrix;
vec3 triWeights( vec3 n ) {
	vec3 w = pow( abs( n ), vec3( uTriSharp ) );
	return w / ( w.x + w.y + w.z );
}
vec4 triSample( sampler2D t, vec3 p, vec3 w ) {
	return texture2D( t, p.yz ) * w.x + texture2D( t, p.zx ) * w.y + texture2D( t, p.xy ) * w.z;
}
float triHash( vec3 p ) {
	p = fract( p * 0.3183099 + 0.1 );
	p *= 17.0;
	return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
}
float triNoise( vec3 x ) {
	vec3 i = floor( x ), f = fract( x );
	f = f * f * ( 3.0 - 2.0 * f );
	return mix(
		mix( mix( triHash( i ), triHash( i + vec3( 1, 0, 0 ) ), f.x ),
		     mix( triHash( i + vec3( 0, 1, 0 ) ), triHash( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
		mix( mix( triHash( i + vec3( 0, 0, 1 ) ), triHash( i + vec3( 1, 0, 1 ) ), f.x ),
		     mix( triHash( i + vec3( 0, 1, 1 ) ), triHash( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
}
float triFbm( vec3 p ) {
	return triNoise( p ) * 0.65 + triNoise( p * 2.7 ) * 0.35;
}`,
      )
      // triP/triW + the wear/grime masks are declared here (the map stage is
      // the earliest consumer in main) and reused by the roughness stage.
      // This replacement always executes, even for map-less paint materials.
      .replace(
        '#include <map_fragment>',
        `vec3 triP = vTriPos * uTriScale;
vec3 triW = triWeights( normalize( vTriNorm ) );
#ifdef USE_MAP
	diffuseColor *= triSample( map, triP, triW );
#endif
// -- Phase 3 detail: mineral streaks, curvature edge wear, streaked grime --
float triCurv = length( fwidth( normalize( vTriNorm ) ) ) / max( length( fwidth( vTriPos ) ), 1e-4 );
float triWearM = uWearAmt * smoothstep( 0.55, 1.4,
	triCurv * 0.5 + ( triFbm( vTriPos * 6.0 + uTriSeed ) - 0.5 ) * 1.1 + uWearAmt * 0.35 );
float triGrimeM = uGrimeAmt * smoothstep( 0.42, 0.85,
	triFbm( vTriPos * uGrimeAniso + uTriSeed ) );
// triFbm is two octaves averaged, so it clusters near 0.5 and rarely passes
// 0.75; the grime window above is placed for that and the streak window has to
// be too, or the streaks simply never fire.
float triAccentM = uAccentAmt * smoothstep( 0.46, 0.66,
	triFbm( vTriPos * uAccentScale + uTriSeed ) );
// mineralogy sits under the wear and dirt that happen TO the surface
diffuseColor.rgb = mix( diffuseColor.rgb, uAccentColor, triAccentM );
diffuseColor.rgb = mix( diffuseColor.rgb, uWearColor, triWearM );
diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * vec3( 0.42, 0.39, 0.35 ), triGrimeM );`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `float roughnessFactor = roughness;
#ifdef USE_ROUGHNESSMAP
	roughnessFactor *= triSample( roughnessMap, triP, triW ).g;
#endif
// chipped edges are smoother bare metal; grime is matte
roughnessFactor = clamp( roughnessFactor - triWearM * 0.45 + triGrimeM * 0.22, 0.05, 1.0 );`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#ifdef USE_EMISSIVEMAP
	totalEmissiveRadiance *= triSample( emissiveMap, triP, triW ).rgb;
#endif`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#ifdef USE_NORMALMAP
	vec3 triGN = normalize( vTriNorm );
	vec3 tnx = texture2D( normalMap, triP.yz ).xyz * 2.0 - 1.0;
	vec3 tny = texture2D( normalMap, triP.zx ).xyz * 2.0 - 1.0;
	vec3 tnz = texture2D( normalMap, triP.xy ).xyz * 2.0 - 1.0;
	tnx.xy *= normalScale;
	tny.xy *= normalScale;
	tnz.xy *= normalScale;
	// whiteout blend: fold the geometry normal into each projection, swizzle to object space
	tnx = vec3( tnx.xy + triGN.zy, abs( tnx.z ) * triGN.x );
	tny = vec3( tny.xy + triGN.xz, abs( tny.z ) * triGN.y );
	tnz = vec3( tnz.xy + triGN.xy, abs( tnz.z ) * triGN.z );
	vec3 triN = normalize( tnx.zyx * triW.x + tny.xzy * triW.y + tnz.xyz * triW.z );
	normal = normalize( normalMatrix * triN ) * faceDirection;
#endif`,
      );
  };
  // All triplanar materials share one shader body (uniforms differ per
  // instance) — a constant key lets three's program cache reuse the program
  // across every hull/greeble/armor/radiator material in the fleet, and across
  // the rocks. Every option above is a uniform rather than a #define precisely
  // so it stays that way: three appends this key to one built from the
  // material's defines, so a uniform-only difference costs no program.
  // Bump the version whenever the shader BODY changes, never for an option.
  mat.customProgramCacheKey = () => 'triplanar-v3';
}
